"use client";

import { DATE_TIME_COPY } from "config/locationCopy";
import { Fragment, useEffect, useState } from "react";
import { FC } from "react";
import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
} from "@headlessui/react";
import {
  ClockIcon,
  MagnifyingGlassIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";
import toast from "react-hot-toast";
import { useLocation, useNavigate } from "react-router-dom";
import { useData } from "data/data-provider";
import moment from "moment";

export interface RentalCarTimesRangeInputProps {
  className?: string;
  fieldClassName?: string;
  hasButtonSubmit?: boolean;
  dropOffLocationType?: boolean;
  startTime: any;
  endTime: any;
  setStartTime: any;
  setEndTime: any;
  isStartDateLocked?: boolean;
}

const timeSlots = Array.from({ length: 24 }, (_, h) => [
  `${h.toString().padStart(2, "0")}:00`,
  `${h.toString().padStart(2, "0")}:30`,
]).flat();

const RentalCarTimesRangeInput: FC<RentalCarTimesRangeInputProps> = ({
  className = "",
  fieldClassName = "[ nc-hero-field-padding ]",
  hasButtonSubmit = true,
  startTime,
  endTime,
  setStartTime,
  setEndTime,
  isStartDateLocked = false,
}) => {
  const {
    startDate,
    setStartDate,
    setEndDate,
    endDate,
    ourCompany,
    partiallyUnavailableTimes,
    pickupLocation,
    dropOffLocation,
    dropoffLocationType,
  } = useData();

  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  // Effective dropoff location for operational hours check
  const effectiveDropoffLocation =
    dropoffLocationType === false && dropOffLocation
      ? dropOffLocation
      : pickupLocation;

  // Returns true when a time slot is outside the location's operational window
  const isOutsideOperationalHours = (
    time: string,
    opStart: string | null | undefined,
    opEnd: string | null | undefined,
  ): boolean => {
    if (!opStart || !opEnd) return false;
    const t = moment(time, "HH:mm");
    return (
      t.isBefore(moment(opStart, "HH:mm")) ||
      t.isAfter(moment(opEnd, "HH:mm"))
    );
  };

  // Intentionally no snap-to-operational-hours effect here: in edit mode the
  // booking's saved times are authoritative; out-of-hours slots stay visually
  // disabled in the picker so the user is still informed.

  const shouldBlockTime =
    location.pathname === "/listing-car-detail" ||
    location.pathname === "/booking-details" ||
    location.pathname === "/car-listing-detail";

  useEffect(() => {
    validateEndTime();
  }, [startDate]);

  const validateEndTime = () => {
    if (
      startDate &&
      endDate &&
      startDate.toDateString() === endDate.toDateString()
    ) {
      const [startHour, startMinute] = startTime.split(":").map(Number);
      const [endHour, endMinute] = endTime.split(":").map(Number);

      const startTimeObj = new Date();
      startTimeObj.setHours(startHour, startMinute);

      const endTimeObj = new Date();
      endTimeObj.setHours(endHour, endMinute);
    }
  };

  const formatTime = (time: string) => {
      const use24Hour = ourCompany?.default_time_format === "24_hour";
  
      if (time === "00:00") {
        return use24Hour ? "00:00" : "Midnight";
      }
  
      return use24Hour
        ? moment(time, "HH:mm").format("HH:mm")
        : moment(time, "HH:mm").format("h:mm A");
  };

  const renderInput = () => {
    return (
      <>
        <div className="text-text-color ">
          <ClockIcon className="w-5 h-5 lg:w-7 lg:h-7" />
        </div>
        <div className="flex-grow text-left text-text-color">
          <span className={`block xl:text-lg font-semibold `}>
            {`${formatTime(startTime)}`} - {`${formatTime(endTime)}`}
          </span>
          <span className={`block mt-1 text-sm leading-none font-light text-text-color`}>
            {isStartDateLocked
              ? "Pickup Time Locked · Select Drop off Time"
              : DATE_TIME_COPY.timeRange}
          </span>
        </div>
      </>
    );
  };

  useEffect(() => {
    if (startDate && endDate) {
      const startDateTime = moment(
        `${moment(startDate).format("YYYY-MM-DD")} ${startTime}`,
        "YYYY-MM-DD HH:mm"
      ).toDate();

      const endDateTime = moment(
        `${moment(endDate).format("YYYY-MM-DD")} ${endTime}`,
        "YYYY-MM-DD HH:mm"
      ).toDate();

      setStartDate(startDateTime);
      setEndDate(endDateTime);
    }
  }, [startTime, endTime]);

  type TimeRange = {
    from: string;
    to: string;
  };

  type PartialUnavailability = {
    [date: string]: TimeRange[];
  };

  const getPartialUnavailableRanges = (
    date: Date | null | undefined,
  ): TimeRange[] => {
    if (!date) return [];
    const m = moment(date);
    if (!m.isValid()) return [];
    const dateKey = m.format("YYYY-MM-DD");
    const ranges = partiallyUnavailableTimes[dateKey];
    return Array.isArray(ranges) ? ranges : [];
  };

  const isTimeBlocked = (
    date: Date | null | undefined,
    time: string,
  ): boolean => {
    if (!date) return false;
    const ranges = getPartialUnavailableRanges(date);
    const timeMoment = moment(time, "HH:mm");
    return ranges.some(({ from, to }) => {
      const fromMoment = moment(from, "HH:mm:ss");
      const toMoment = moment(to, "HH:mm:ss");
      return (
        timeMoment.isSameOrAfter(fromMoment) &&
        timeMoment.isSameOrBefore(toMoment)
      );
    });
  };

  // Find next slot that is neither booking-blocked nor outside operational hours.
  // Returns null when every operational-hour slot is also blocked.
  const getNextAvailableTime = (
    date: Date | null | undefined,
    time: string,
    slots: string[],
    opStart?: string | null,
    opEnd?: string | null,
  ): string | null => {
    if (!date) return time;

    const isOpBlocked = (t: string): boolean =>
      isOutsideOperationalHours(t, opStart, opEnd);
    // Wrap-around scan from beginning
    for (let i = 0; i < slots.length; i++) {
      if (!isTimeBlocked(date, slots[i]) && !isOpBlocked(slots[i])) return slots[i];
    }

    // No valid slot within operational hours
    return null;
  };

  useEffect(() => {
    // 1NOW-1162: when the pick-up is locked (in-progress / already-started
    // booking) the saved pick-up is authoritative and can't be changed — so
    // never snap it to operational hours or, worse, error and clear both dates
    // because the locked pick-up falls in a blocked / out-of-hours slot. That
    // was surfacing "No available time slot … Operational hours …" and wiping
    // the dates, blocking the edit. Only the drop-off (which the renter is
    // actually choosing) is validated below.
    if (
      !isStartDateLocked &&
      startDate &&
      startTime &&
      isTimeBlocked(startDate, startTime)
    ) {
      const next = getNextAvailableTime(
        startDate,
        startTime,
        timeSlots,
        pickupLocation?.operational_start_time,
        pickupLocation?.operational_end_time,
      );
      if (next === null) {
        const opStart = pickupLocation?.operational_start_time;
        const opEnd = pickupLocation?.operational_end_time;
        toast.error(
          opStart && opEnd
            ? `No available time slot on the selected date. Operational hours: ${formatTime(opStart)} – ${formatTime(opEnd)}.`
            : "No available time slot on the selected date."
        );
        setStartDate(null);
        setEndDate(null);
      } else {
        setStartTime(next);
      }
    }
    if (endDate && endTime && isTimeBlocked(endDate, endTime)) {
      const next = getNextAvailableTime(
        endDate,
        endTime,
        timeSlots,
        effectiveDropoffLocation?.operational_start_time,
        effectiveDropoffLocation?.operational_end_time,
      );
      if (next === null) {
        const opStart = effectiveDropoffLocation?.operational_start_time;
        const opEnd = effectiveDropoffLocation?.operational_end_time;
        toast.error(
          opStart && opEnd
            ? `No available time slot on the selected date. Operational hours: ${formatTime(opStart)} – ${formatTime(opEnd)}.`
            : "No available time slot on the selected date."
        );
        // 1NOW-1162: never clear a locked pick-up — only reset the drop-off the
        // renter is actually choosing. Clearing both wiped the locked pickup of
        // an in-progress booking.
        if (!isStartDateLocked) {
          setStartDate(null);
        }
        setEndDate(null);
      } else {
        setEndTime(next);
      }
    }
  }, [startDate, endDate]);

  return (
    <>
      <Popover
        className={`RentalCarDatesRangeInput relative flex ${className}`}
      >
        {({ open }) => {
          return (
          <>
            <div
                className={`flex-1 z-10  flex items-center focus:outline-none ${
                  open ? "nc-hero-field-focused !rounded-md" : ""
              }`}
            >
              <PopoverButton
                  className={`flex-1 z-10 flex relative ${fieldClassName} items-center space-x-3 focus:outline-none `}
                onClickCapture={() => document.querySelector("html")?.click()}
              >
                  {renderInput()}
              </PopoverButton>
            </div>

            <Transition
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
            >
                <PopoverPanel className="absolute left-1/2 z-10 mt-3 top-full w-screen max-w-sm -translate-x-1/2 transform px-4 sm:px-0 lg:max-w-l">
                  <div className="p-4 space-x-2 overflow-hidden rounded-md shadow-lg ring-1 ring-black ring-opacity-5 bg-secondary-50">
                    {isStartDateLocked && (
                      <div className="mb-3 p-2 bg-yellow-50 border border-yellow-200 rounded-md text-xs text-yellow-800 text-center">
                      Pickup time is locked
                    </div>
                  )}
                    <div className="grid grid-cols-2">
                    <div>
                        <h3 className="text-lg font-medium flex justify-center">
                        Start Time
                      </h3>

                      <div className="overflow-y-auto h-48">
                        {timeSlots.map((time) => {
                          const isUnavailable = shouldBlockTime
                            ? isTimeBlocked(startDate ?? null, time)
                            : false;
                          const isLocked = isStartDateLocked;
                          const isOperationallyBlocked = isOutsideOperationalHours(
                            time,
                            pickupLocation?.operational_start_time,
                            pickupLocation?.operational_end_time,
                          );

                          return (
                            <div
                              key={time}
                                className={`cursor-pointer text-center py-2 px-4 mb-1 rounded-full transition ${
                                  startTime === time
                                    ? "bg-primary-200 text-white"
                                    : ""
                              } ${
                                isUnavailable || isLocked || isOperationallyBlocked
                                  ? "opacity-50 cursor-not-allowed line-through pointer-events-none"
                                  : ""
                              }`}
                              onClick={() => {
                                  if (isLocked) {
                                    toast.error(
                                      "Pickup time is locked and cannot be changed"
                                    );
                                    return;
                                  }

                                  if (isUnavailable) {
                                    toast.error(
                                      "This time slot is unavailable due to a partial booking"
                                    );
                                    return;
                                  }

                                  if (isOperationallyBlocked) {
                                    toast.error(
                                      "This time is outside the location's operational hours"
                                    );
                                    return;
                                  }
                                setStartTime(time);
                              }}
                            >
                              {formatTime(time)}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <div>
                        <h3 className="text-lg font-medium flex justify-center">
                        End Time
                      </h3>
                      <div className="overflow-y-auto h-48">
                        {timeSlots.map((time) => {
                          const isUnavailable = shouldBlockTime
                            ? isTimeBlocked(endDate ?? null, time)
                            : false;
                          const isOperationallyBlocked = isOutsideOperationalHours(
                            time,
                            effectiveDropoffLocation?.operational_start_time,
                            effectiveDropoffLocation?.operational_end_time,
                          );

                          return (
                            <div
                              key={time}
                                className={`cursor-pointer text-center py-2 px-4 mb-1 rounded-full transition ${
                                  endTime === time
                                    ? "bg-primary-200 text-white"
                                    : ""
                                } ${
                                isUnavailable || isOperationallyBlocked
                                    ? "opacity-50 cursor-not-allowed line-through"
                                  : ""
                              }`}
                              onClick={() => {
                                  if (isUnavailable) {
                                    toast.error(
                                      "This time slot is unavailable due to a partial booking"
                                    );
                                    return;
                                  }

                                  if (isOperationallyBlocked) {
                                    toast.error(
                                      "This time is outside the location's operational hours"
                                    );
                                    return;
                                  }
                                setEndTime(time);
                              }}
                            >
                              {formatTime(time)}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    </div>
                  <PopoverButton className="mt-6 w-full bg-primary-100 hover:bg-primary-200 text-white font-semibold py-3 rounded-xl transition">
                      Save
                  </PopoverButton>
                </div>
                </PopoverPanel>
            </Transition>
          </>
          );
        }}
      </Popover>
    </>
  );
};

export default RentalCarTimesRangeInput;